import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, AlertTriangle } from 'lucide-react';
import { fetchFundingRates } from '../lib/marketData';
import type { FundingRateSimple } from '../lib/coinglass';

interface FundingRatesGridProps {
  symbol?: string;
}

const FundingRatesGrid: React.FC<FundingRatesGridProps> = ({ symbol = 'BTC' }) => {
  const [fundingRates, setFundingRates] = useState<FundingRateSimple[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadFundingRates = async () => {
    try {
      setError(null);
      const data = await fetchFundingRates(symbol);
      setFundingRates(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load funding rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFundingRates();
    
    // Refresh every 2 minutes
    const interval = setInterval(loadFundingRates, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [symbol]);

  const formatFundingRate = (rate: number) => {
    const percentage = rate * 100;
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(4)}%`;
  };

  const formatTimeToNext = (timestamp: number) => {
    const now = Date.now();
    const diff = timestamp - now;
    
    if (diff <= 0) return 'Now';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getFundingRateColor = (rate: number) => {
    if (rate > 0.01) return 'text-red-400'; // High positive (expensive for longs)
    if (rate > 0) return 'text-orange-400'; // Positive
    if (rate > -0.01) return 'text-green-400'; // Negative (cheaper for longs)
    return 'text-green-500'; // Very negative
  };

  const getFundingRateBg = (rate: number) => {
    if (rate > 0.01) return 'bg-red-500/10 border-red-500/20';
    if (rate > 0) return 'bg-orange-500/10 border-orange-500/20';
    if (rate > -0.01) return 'bg-green-500/10 border-green-500/20';
    return 'bg-green-600/10 border-green-600/20';
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Funding Rates - {symbol}</h2>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
        </div>
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-zinc-800 rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-zinc-700 rounded mb-2"></div>
              <div className="h-6 bg-zinc-700 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Funding Rates - {symbol}</h2>
          <button
            onClick={loadFundingRates}
            className="flex items-center px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
        <div className="flex items-center text-red-400">
          <AlertTriangle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const avgFundingRate = fundingRates.length > 0 
    ? fundingRates.reduce((sum, rate) => sum + rate.fundingRate, 0) / fundingRates.length 
    : 0;

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Funding Rates - {symbol}</h2>
          <div className="flex items-center mt-2 text-sm text-gray-400">
            <span>Average: </span>
            <span className={`ml-1 font-medium ${getFundingRateColor(avgFundingRate)}`}>
              {formatFundingRate(avgFundingRate)}
            </span>
            {lastUpdated && (
              <span className="ml-4">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={loadFundingRates}
          className="flex items-center px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {fundingRates.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No funding rates data available for {symbol}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fundingRates.map((rate) => (
            <div
              key={rate.exchangeName}
              className={`rounded-lg border p-4 transition-all hover:scale-105 ${getFundingRateBg(rate.fundingRate)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">{rate.exchangeName}</span>
                {rate.fundingRate > 0 ? (
                  <TrendingUp className="w-4 h-4 text-red-400" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-green-400" />
                )}
              </div>
              
              <div className={`text-2xl font-bold mb-2 ${getFundingRateColor(rate.fundingRate)}`}>
                {formatFundingRate(rate.fundingRate)}
              </div>
              
              <div className="text-sm text-gray-400">
                <div>Next funding: {formatTimeToNext(rate.nextFundingTime)}</div>
                {rate.predicatedRate !== rate.fundingRate && (
                  <div className="mt-1">
                    Predicted: 
                    <span className={`ml-1 ${getFundingRateColor(rate.predicatedRate)}`}>
                      {formatFundingRate(rate.predicatedRate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-white mb-2">Understanding Funding Rates</h3>
        <div className="text-xs text-gray-400 space-y-1">
          <div>🟢 <span className="text-green-400">Negative rates</span>: Shorts pay longs (bullish sentiment)</div>
          <div>🟡 <span className="text-orange-400">Low positive rates</span>: Normal market conditions</div>
          <div>🔴 <span className="text-red-400">High positive rates</span>: Longs pay shorts (high leverage/greed)</div>
        </div>
      </div>
    </div>
  );
};

export default FundingRatesGrid;
